/**
 * Checked-in provider list prices of every catalog brain (docs/ECONOMICS.md "Model rate cards"), in USD per million
 * tokens. `syncCatalog` (`pnpm db:migrate`, `pnpm db:seed`) turns them into `billing.rate_cards` rows; nothing reads
 * prices from the network at runtime. `pnpm --filter @algoboo/model-gateway pricing:refresh` fetches OpenRouter's
 * model list and prints the differences against this table; it never edits it. To change a price: edit the entry,
 * set its `asOf` to the day of the change and append that day to `PRICING_DATES` (older list cards are closed by the
 * sync and replaced by the new one).
 *
 * Sources: Anthropic list prices (claude-api reference, cross-checked against OpenRouter's `anthropic/*` listing);
 * the others from https://openrouter.ai/api/v1/models on the `asOf` day. OpenRouter bills the upstream list price per
 * token (its fee is charged on credit purchases, not per token), so the same table prices direct and routed calls.
 */

/** Prices of one model in USD per million tokens. */
export interface ListPrice {
  /** Uncached prompt tokens. */
  input: number
  output: number
  /** Prompt tokens read from the provider cache. */
  cacheRead: number
  /** Prompt tokens written to the provider cache (Anthropic: 5-minute TTL; implicit caches: the input price). */
  cacheWrite: number
  /** Anthropic 1-hour cache writes; `undefined` = 2x input. */
  cacheWrite1h?: number
  /** Day these prices were read (`YYYY-MM-DD`); part of the list card's deterministic id. */
  asOf: string
  /** OpenRouter model id serving this brain (also the id `pricing:refresh` compares against). */
  openrouterId: string
  /**
   * Long-prompt tier of the provider (prompts at or above `minPromptTokens` cost more). Documentation only: the rate
   * card carries the base tier; gateway calls are interpretation-sized, far below every threshold.
   */
  longContext?: { minPromptTokens: number; input: number; output: number; cacheRead?: number }
}

/** Every day a list price in this table was read; the sync closes current list cards of earlier days. */
export const PRICING_DATES = ['2026-06-24', '2026-09-25'] as const

/** Latest pricing day (the list-card date of brains priced on it). */
export const PRICING_AS_OF = PRICING_DATES[PRICING_DATES.length - 1]!

const A = '2026-09-25'

/** List prices per brain id (every catalog brain, enabled or not). */
export const PRICING: Record<string, ListPrice> = {
  // Anthropic's models, served through OpenRouter by `openrouterId` (OpenRouter only).
  'anthropic/claude-fable-5-1': { input: 10, output: 50, cacheRead: 0.25, cacheWrite: 12.5, cacheWrite1h: 20, asOf: A, openrouterId: 'anthropic/claude-fable-5.1' },
  'anthropic/claude-opus-5-5': { input: 4, output: 20, cacheRead: 0.2, cacheWrite: 5, cacheWrite1h: 8, asOf: A, openrouterId: 'anthropic/claude-opus-5.5' },
  'anthropic/claude-sonnet-5': { input: 2, output: 10, cacheRead: 0.2, cacheWrite: 2.5, cacheWrite1h: 4, asOf: A, openrouterId: 'anthropic/claude-sonnet-5' },
  'anthropic/claude-haiku-4-5-20251001': { input: 1, output: 5, cacheRead: 0.1, cacheWrite: 1.25, cacheWrite1h: 2, asOf: A, openrouterId: 'anthropic/claude-haiku-4.5' },
  // OpenRouter-only brains (implicit prompt caching: writes at the input price unless the provider lists one).
  'openrouter/openai/gpt-5': { input: 1.25, output: 10, cacheRead: 0.125, cacheWrite: 1.25, asOf: A, openrouterId: 'openai/gpt-5' },
  'openrouter/x-ai/grok-4.7': {
    input: 1.6,
    output: 4.8,
    cacheRead: 0.4,
    cacheWrite: 1.6,
    asOf: A,
    openrouterId: 'x-ai/grok-4.7',
    longContext: { minPromptTokens: 200_000, input: 3.2, output: 9.6, cacheRead: 0.8 },
  },
  'openrouter/deepseek/deepseek-v4-pro': { input: 0.783, output: 1.566, cacheRead: 0.06525, cacheWrite: 0.783, asOf: A, openrouterId: 'deepseek/deepseek-v4-pro' },
  'openrouter/google/gemini-2.5-pro': {
    input: 1.25,
    output: 10,
    cacheRead: 0.125,
    cacheWrite: 0.375,
    asOf: A,
    openrouterId: 'google/gemini-2.5-pro',
    longContext: { minPromptTokens: 200_000, input: 2.5, output: 15, cacheRead: 0.25 },
  },
  'openrouter/google/gemini-2.5-flash': { input: 0.3, output: 2.5, cacheRead: 0.03, cacheWrite: 0.08333, asOf: A, openrouterId: 'google/gemini-2.5-flash' },
  'openrouter/z-ai/glm-5.3-flash': { input: 0.045, output: 0.14, cacheRead: 0.01, cacheWrite: 0.045, asOf: A, openrouterId: 'z-ai/glm-5.3-flash' },
}

/**
 * Micro-credits per 1k tokens for a USD-per-million-tokens price. 1 credit = USD 0.01 and 1 000 000 micro = 1 credit,
 * so USD 1 = 100 000 000 micro and USD X / 1M tokens = X × 100 000 micro / 1k tokens (exact to USD 0.00001 / MTok).
 */
export function microPer1kFromUsdPerMTok(usdPerMTok: number): bigint {
  const micro = Math.round(usdPerMTok * 100_000)
  if (!Number.isFinite(usdPerMTok) || micro <= 0) throw new Error(`invalid list price ${usdPerMTok}`)
  return BigInt(micro)
}

/** USD per million tokens of an OpenRouter per-token price string (`"0.00000125"` → 1.25), rounded to 1e-6. */
export function usdPerMTokFromPerToken(perToken: string | number | null | undefined): number | undefined {
  if (perToken === null || perToken === undefined || perToken === '') return undefined
  const n = Number(perToken)
  if (!Number.isFinite(n) || n < 0) return undefined
  return Math.round(n * 1e12) / 1e6
}
